/**
 * Runtime deployment config loader (internal-cloud readiness, 2026-06-10).
 *
 * WHY THIS EXISTS
 * ---------------
 * `VITE_FLEET_API_URL` / `VITE_FLEET_WS_URL` / the three `VITE_MAP_*` knobs
 * are baked into the bundle at build time (`import.meta.env`), so repointing
 * the SPA at a different fleet-api (e.g. an internal, closed-network cloud on
 * a Windows host) used to require a full rebuild. This module adds an
 * OPTIONAL, boot-time `/config.json` fetch that overrides those values at
 * runtime: ONE build artifact can now serve both the public DigitalOcean
 * deployment AND any number of internal deployments, each repointed by a
 * tiny static JSON file dropped next to index.html.
 *
 * CONTRACT (all fields optional; unknown fields ignored):
 *   {
 *     "fleetApiUrl":          "http://10.0.0.5:8000",
 *     "fleetWsUrl":           "ws://10.0.0.5:8000",
 *     "mapSatelliteTilesUrl": "http://10.0.0.5:8080/sat/{z}/{y}/{x}",
 *     "mapOsmTilesUrl":       "http://10.0.0.5:8080/osm/{z}/{x}/{y}.png",
 *     "mapVectorStyleUrl":    "http://10.0.0.5:8080/styles/liberty.json"
 *   }
 * See `public/config.json.example` for the operator-facing template.
 *
 * FALLBACK GUARANTEE (the DigitalOcean deployment MUST be unaffected):
 * the loader NEVER throws and resolves to "no overrides" in every failure
 * mode. The interesting failure mode is an SPA-history-fallback host (DO App
 * Platform's catchall_document) answering GET /config.json with a 200
 * text/html copy of index.html; we therefore require BOTH res.ok AND a JSON
 * content-type AND a successful JSON.parse into a plain object before any
 * override is applied. When /config.json is absent (the DO deployment ships
 * none), every consumer falls back to the build-time VITE_* values via the
 * lazy getters in `src/lib/env.ts`, which is byte-for-byte the pre-existing
 * behavior.
 *
 * BOOT ORDER: `main.tsx` awaits {@link loadRuntimeConfig} BEFORE the first
 * React render so lazy route chunks (which may capture env values at module
 * scope, e.g. SatelliteSentryMap's tile constants) always evaluate after the
 * overrides are in place. Entry-chunk modules read the fleet URLs at call
 * time (request time / socket-connect time), so pre-config module evaluation
 * is safe there too.
 */

/** Shape of the optional /config.json runtime override file. */
export interface RuntimeConfig {
  /** Fleet API REST base URL override (e.g. "http://10.0.0.5:8000"). */
  fleetApiUrl?: string;
  /** Fleet API WebSocket base URL override (e.g. "ws://10.0.0.5:8000"). */
  fleetWsUrl?: string;
  /** Satellite raster tile template override ({z}/{y}/{x} placeholders). */
  mapSatelliteTilesUrl?: string;
  /** OSM-fallback raster tile template override ({z}/{x}/{y} placeholders). */
  mapOsmTilesUrl?: string;
  /** Maplibre vector style URL override (FrozenMapSnapshot). */
  mapVectorStyleUrl?: string;
}

/** The keys we accept from /config.json; anything else is ignored. */
const RUNTIME_CONFIG_KEYS: readonly (keyof RuntimeConfig)[] = [
  'fleetApiUrl',
  'fleetWsUrl',
  'mapSatelliteTilesUrl',
  'mapOsmTilesUrl',
  'mapVectorStyleUrl',
];

/**
 * Hard cap on how long the boot render may wait for /config.json (ms).
 * A hung or tar-pitted request must never brick the app: after this long
 * we proceed with build-time values (the request keeps running in the
 * background but its result is discarded). 3s is generous for a same-origin
 * static file while still bounding worst-case first-paint delay.
 */
export const RUNTIME_CONFIG_FETCH_TIMEOUT_MS = 3_000;

/**
 * Module-level singleton holding the parsed overrides, or null when no
 * valid /config.json was found (the default on the DO deployment).
 */
let runtimeConfig: RuntimeConfig | null = null;

/**
 * Read the currently-loaded runtime overrides.
 *
 * @returns The parsed RuntimeConfig when a valid /config.json was loaded at
 *   boot, else null (callers fall back to build-time env).
 */
export function getRuntimeConfig(): RuntimeConfig | null {
  return runtimeConfig;
}

/**
 * Validate + narrow an unknown parsed JSON value into a RuntimeConfig.
 *
 * Only own string-valued properties from the known key list are copied;
 * empty strings are ignored (an empty override is meaningless and would
 * otherwise shadow a real build-time value); everything else (numbers,
 * nulls, nested objects, unknown keys) is dropped. Returns null when the
 * input is not a plain object or yields zero usable overrides, so callers
 * treat "config.json existed but was junk/empty" identically to "absent".
 *
 * Exported for direct unit-testing of the narrowing rules.
 *
 * @param raw The JSON.parse output of the /config.json body.
 * @returns A sanitized RuntimeConfig, or null when nothing usable was found.
 */
export function sanitizeRuntimeConfig(raw: unknown): RuntimeConfig | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const source = raw as Record<string, unknown>;
  const out: RuntimeConfig = {};
  let any = false;
  for (const key of RUNTIME_CONFIG_KEYS) {
    const value = source[key];
    if (typeof value === 'string' && value.length > 0) {
      out[key] = value;
      any = true;
    }
  }
  return any ? out : null;
}

/**
 * Fetch + apply /config.json. NEVER rejects; resolves once the runtime
 * config state is settled (either overrides applied or fallback confirmed).
 *
 * Failure modes that resolve to "no overrides" (build-time fallback):
 *   - network error / fetch unavailable (non-browser test environments)
 *   - non-2xx response (plain static hosts 404 the missing file)
 *   - 200 with a non-JSON content-type (SPA catchall returning index.html)
 *   - body that fails JSON.parse or sanitizes to nothing
 *   - response slower than RUNTIME_CONFIG_FETCH_TIMEOUT_MS
 *
 * `cache: 'no-store'` keeps operators in control: editing config.json on the
 * internal host takes effect on the next page load, never pinned by a CDN or
 * disk cache (mirrors the version.json auto-update checker's strategy).
 *
 * @param fetchImpl Injectable fetch for tests; defaults to the global fetch.
 * @returns Promise that always resolves (void).
 */
export async function loadRuntimeConfig(
  fetchImpl: typeof fetch | undefined = typeof fetch !== 'undefined' ? fetch : undefined,
): Promise<void> {
  if (!fetchImpl) {
    runtimeConfig = null;
    return;
  }
  try {
    // Bound the boot-blocking wait. AbortController also cancels the network
    // request itself (not just the await) so a tar-pitted host doesn't hold
    // a socket open for the lifetime of the tab.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RUNTIME_CONFIG_FETCH_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetchImpl('/config.json', {
        cache: 'no-store',
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      runtimeConfig = null;
      return;
    }
    // SPA-fallback guard: DO App Platform answers unknown paths with a 200
    // text/html index.html. Require an explicit JSON content-type before we
    // even attempt to parse, so the catchall can never poison the config.
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('json')) {
      runtimeConfig = null;
      return;
    }
    const parsed: unknown = await res.json();
    runtimeConfig = sanitizeRuntimeConfig(parsed);
  } catch {
    // Network error, abort/timeout, or JSON parse failure: silently fall
    // back to build-time env. This is the expected path on the public DO
    // deployment, which ships no config.json.
    runtimeConfig = null;
  }
}

/**
 * Test-only escape hatch: directly set (or clear with null) the runtime
 * config singleton without going through fetch. Production code must use
 * {@link loadRuntimeConfig}.
 *
 * @param next The override object to install, or null to clear.
 */
export function setRuntimeConfigForTests(next: RuntimeConfig | null): void {
  runtimeConfig = next;
}
